import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, ArrowUpCircle, Receipt } from 'lucide-react';
import { fetchInvoices, downloadInvoicePdfByPurchaseId } from '../api';
import { useLanguage } from '../contexts/LanguageContext';
import ModalMotionShell from './ModalMotionShell';

const DatasetInvoicesModal = ({ isOpen, onClose, year, currentPlan }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);

  const hasContext = !!year;

  useEffect(() => {
    if (!isOpen || !hasContext) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        // Wir filtern im Backend per ?year=...
        const resp = await fetchInvoices(null, year, null);

        const rawList =
          resp?.invoices ||
          resp?.purchases ||
          resp?.data?.invoices ||
          resp?.data?.purchases ||
          [];

        const normalized = rawList.map((inv) => ({
          id: inv.id,
          purchaseId: inv.purchase_id || inv.purchaseId,
          plan: inv.plan,
          year: inv.year,
          totalAmount: Number(inv.total_amount ?? inv.totalAmount ?? 0),
          invoiceNo: inv.invoice_no || inv.invoiceNo,
          purchaseDate: inv.purchase_date || inv.purchaseDate,
          isUpgrade: !!(inv.is_upgrade ?? inv.isUpgrade),
          upgradeFromPlan: inv.upgrade_from_plan || inv.upgradeFromPlan || null,
        }));

        // Nach Datum sortieren: Basis zuerst, Upgrades danach
        normalized.sort((a, b) => {
          const tA = a.purchaseDate ? new Date(a.purchaseDate).getTime() : 0;
          const tB = b.purchaseDate ? new Date(b.purchaseDate).getTime() : 0;
          return tA - tB;
        });

        setInvoices(normalized);
      } catch (err) {
                setError(
          language === 'en'
            ? 'Invoices could not be loaded.'
            : 'Rechnungen konnten nicht geladen werden.'
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, hasContext, year, language]);

  const title =
    language === 'en'
      ? `Invoices for ${year}`
      : `Rechnungen für ${year}`;

  const formatAmount = (value) =>
    new Intl.NumberFormat(language === 'en' ? 'en-US' : 'de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0);

  const formatDate = (value) => {
    if (!value) return '–';
    const d = new Date(value);
    return d.toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const baseLabel = (plan, y) =>
    language === 'en'
      ? `Plan ${plan} (${y})`
      : `Plan ${plan} (${y})`;

  const upgradeLabel = (from, to, y) =>
    language === 'en'
      ? `Upgrade from ${from || 'N/A'} to ${to} (${y})`
      : `Upgrade von ${from || 'N/A'} auf ${to} (${y})`;

  const modal = (
    <ModalMotionShell
      isOpen={isOpen}
      onClose={onClose}
      zIndexClass="z-[10000]"
      backdropTintClass="bg-black/40"
      backdropClassName="px-3 py-3"
    >
      <div
        className="modal-container bg-white rounded-2xl shadow-xl w-full max-w-md p-4 sm:p-5"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-gray-700" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
              {currentPlan && (
                <p className="text-[11px] text-gray-500">
                  {language === 'en'
                    ? `Current plan: ${currentPlan}`
                    : `Aktueller Plan: ${currentPlan}`}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        {loading && (
          <div className="py-6 text-center text-sm text-gray-500">
            {language === 'en' ? 'Loading invoices…' : 'Lade Rechnungen…'}
          </div>
        )}

        {!loading && error && (
          <div className="py-4 text-center text-sm text-red-500">{error}</div>
        )}

        {!loading && !error && invoices.length === 0 && (
          <div className="py-6 text-center text-sm text-gray-500">
            {language === 'en'
              ? 'No invoices for this year yet.'
              : 'Für dieses Jahr liegen noch keine Rechnungen vor.'}
          </div>
        )}

        {!loading && !error && invoices.length > 0 && (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {invoices.map((inv) => {
              const isUpgrade = inv.isUpgrade;
              const label = isUpgrade
                ? upgradeLabel(inv.upgradeFromPlan, inv.plan, inv.year)
                : baseLabel(inv.plan, inv.year);

              const typeBadge = isUpgrade
                ? language === 'en'
                  ? 'Upgrade'
                  : 'Upgrade'
                : language === 'en'
                ? 'Base purchase'
                : 'Hauptkauf';

              const TypeIcon = isUpgrade ? ArrowUpCircle : FileText;

              return (
                <div
                  key={inv.purchaseId || inv.id}
                  className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <TypeIcon className="w-4 h-4 text-gray-600" />
                    <div>
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-[11px] text-gray-500">
                        {typeBadge} •{' '}
                        {formatDate(inv.purchaseDate)}
                        {inv.invoiceNo
                          ? ` • #${inv.invoiceNo}`
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-semibold text-gray-900">
                      {formatAmount(inv.totalAmount)}
                    </span>
                    <button
                      onClick={() =>
                        downloadInvoicePdfByPurchaseId(
                          inv.purchaseId,
                          language
                        )
                      }
                      className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                    >
                      {language === 'en' ? 'Download' : 'Download'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModalMotionShell>
  );

  return createPortal(modal, document.body);
};

export default DatasetInvoicesModal;

